// Async render from a manifest. Source-aware: URL sources fetch + sample
// the PNG; synthetic sources synthesise a brightness field from the
// generative variant + seed without any fetch.
//
// Browser-only by default (uses cachedLoadImageFromUrl, which depends on
// `document` / `Image` for URL sources). Synthetic sources work in any
// runtime — Node-side rasteriser invokes `synthesiseSampledImage` directly.

import { buildVariantContext, getBrightnessFn } from '../core/brightness-fns';
import { cachedLoadImageFromUrl } from '../core/browser/image-cache';
import { isDotStudioMode } from '../core/dot-engine/types';
import { sampleGenerativeBrightness } from '../core/generative-brightness';
import { renderPattern } from '../renderers';
import type { GenerativeVariant, Render2DContext, SampledImage } from '../types';
import { type ManifestToStateOverrides, manifestToState } from './converters';
import { halftoneImgModeForTheme } from './theme-halftone';
import type { ManifestSource, PatternManifest } from './types';
import type { ManifestThemeName } from './url-hash';

/**
 * Telemetry hook fired when a manifest references something the lib no
 * longer knows about (e.g. a deleted preset name) or fails to fetch.
 */
export type ManifestFallbackEvent =
  | { kind: 'unknown-preset'; name: string }
  | { kind: 'unknown-outcome-hue'; hue: string }
  | { kind: 'unknown-category'; category: string }
  // ALLOW-UNKNOWN: discriminated event payload — caller narrows on .kind
  | { kind: 'source-url-failed'; sourceUrl: string; error: unknown };

export interface RenderManifestOptions {
  onFallback?: ((event: ManifestFallbackEvent) => void) | undefined;
  /**
   * Optional resolution for the synthetic brightness field. Higher = more
   * detail; default 256 is plenty for mosaic dither at any output size.
   */
  syntheticResolution?: number | undefined;
  /**
   * Workspace-only debug override — when set, replaces the lib's
   * DEFAULT_PARAMS / DEFAULT_STATE.mosaic with these values for this
   * single render. Production callsites should NEVER set this — leaving
   * it undefined preserves the propagation contract (lib changes flow
   * to every existing entity).
   */
  debugOverride?: ManifestToStateOverrides | undefined;
  /** App chrome theme — drives halftone dot polarity. Defaults to document class. */
  theme?: ManifestThemeName | undefined;
}

function detectBrowserTheme(): ManifestThemeName {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

const DEFAULT_SYNTHETIC_RESOLUTION = 256;

/**
 * Synthesise a `SampledImage` by evaluating a brightness function across
 * a grid. Pure CPU work — no fetch, no DOM. Used when manifest.source.kind
 * is 'synthetic'.
 */
export function synthesiseSampledImage(
  variant: GenerativeVariant,
  seed: number,
  resolution: number = DEFAULT_SYNTHETIC_RESOLUTION,
  aspectWidth: number = 3,
  aspectHeight: number = 1,
): SampledImage {
  const sampleW = resolution;
  const sampleH = Math.max(1, Math.round((resolution * aspectHeight) / aspectWidth));
  const ctx = buildVariantContext(seed);
  const brightness = new Float32Array(sampleW * sampleH);
  const colorData = new Uint8Array(sampleW * sampleH * 3);
  for (let y = 0; y < sampleH; y++) {
    const v = sampleH === 1 ? 0 : y / (sampleH - 1);
    for (let x = 0; x < sampleW; x++) {
      const u = sampleW === 1 ? 0 : x / (sampleW - 1);
      const b = isDotStudioMode(variant)
        ? sampleGenerativeBrightness(u, v, variant, seed, sampleW, sampleH)
        : getBrightnessFn(variant)(u, v, ctx);
      const idx = y * sampleW + x;
      brightness[idx] = b;
      const grey = Math.max(0, Math.min(255, Math.round(b * 255)));
      const ci = idx * 3;
      colorData[ci] = grey;
      colorData[ci + 1] = grey;
      colorData[ci + 2] = grey;
    }
  }
  return {
    brightness,
    colorData,
    sampleW,
    sampleH,
    width: sampleW,
    height: sampleH,
    dataURL: null,
  };
}

async function loadSampledFromSource(
  source: ManifestSource,
  options: RenderManifestOptions,
): Promise<SampledImage> {
  if (source.kind === 'synthetic') {
    return synthesiseSampledImage(source.variant, source.seed, options.syntheticResolution);
  }
  try {
    return await cachedLoadImageFromUrl(source.url);
  } catch (err: unknown) {
    options.onFallback?.({ kind: 'source-url-failed', sourceUrl: source.url, error: err });
    throw err;
  }
}

/**
 * Render a pattern to a 2D canvas context from a manifest.
 *
 * For URL sources: fetches the source PNG, samples it, renders through
 * the image-overlay pipeline.
 * For synthetic sources: evaluates the brightness function inline, packs
 * the result into a SampledImage, renders the same pipeline. No fetch.
 *
 * Re-throws on URL-source fetch failure (after firing `onFallback`) — image
 * overlays can't render without a source, and the caller is in the best
 * position to render their own placeholder.
 */
export async function renderManifest(
  ctx: Render2DContext,
  width: number,
  height: number,
  manifest: PatternManifest,
  options: RenderManifestOptions = {},
): Promise<void> {
  const sampled = await loadSampledFromSource(manifest.source, options);
  const baseState = manifestToState(manifest, sampled, options.debugOverride);
  const theme = options.theme ?? detectBrowserTheme();
  const state = {
    ...baseState,
    params: {
      ...baseState.params,
      imgMode: halftoneImgModeForTheme(theme),
    },
  };
  renderPattern(ctx, width, height, state);
}
